import React from 'react';
import {
  Box,
  Grid,
  Typography,
  TextField,
  InputAdornment,
} from '@mui/material';
import { SearchIcons } from '../../../../../../../icons/icons';
import { Button, DataTable } from '../../../../../../../ui';
import { CustomSelect } from '../../../../../../../ui/custom_react_select/custom_select';
import { SearchIcon } from '../../../../../../../ui/icons/icons';
import {
  CandidateJobApplicantsData,
  sortCandidates,
} from '../../../../../../../utils/types/JobOpenings';
import { SelectOptions } from '../../../../../../../utils/types/react_select_types';
import { getColumns } from './Functions/GetColumns';

export interface CandidateListType {
  enabled: boolean;
  base64Encoded?: string;
  firstName: string;
  lastName: string;
  email: string;
  id: string;
  applicantStatus: string;
  invited: boolean;
  assessmentStatus: string;
  userAssessmentId?: string;
}

interface CandidateInvitesProps {
  handleSendInvites: () => void;
  handleRemoveCandidate: (targetId?: string) => void;
  candidatesList: CandidateListType[];
  loading: boolean;
  pageNumber: number;
  totalPages: number;
  totalRecords: number;
  selectedCandidates: string[];
  selectedCandidateIds: Set<string>;
  limit: number;
  handleSortChange: (key: string, direction: 'asc' | 'desc') => void;
  isRowSelected: (rowIndex: number, row: CandidateListType) => boolean;
  candidateInviteObject: CandidateJobApplicantsData;
  handleSelectRow: (rowIndex: number, row: CandidateListType) => void;
  sort: sortCandidates;
  handleSelectAll: () => void;
  handleStatusListChange: (
    label: string,
    selectedValue: SelectOptions[],
  ) => void;
  onPageChange: (page: number) => void;
  setLocalName: (localName: string) => void;
  setLocalEmail: (localEmail: string) => void;
  localName: string;
  localEmail: string;
  isAllSelected: boolean;
  isIndeterminate: boolean;
}

export const CandidateInvites: React.FC<CandidateInvitesProps> = ({
  handleSendInvites,
  handleRemoveCandidate,
  candidatesList,
  loading,
  pageNumber,
  totalPages,
  totalRecords,
  selectedCandidates,
  limit,
  handleSortChange,
  isRowSelected,
  candidateInviteObject,
  handleSelectRow,
  sort,
  handleSelectAll,
  handleStatusListChange,
  onPageChange,
  setLocalName,
  setLocalEmail,
  localName,
  localEmail,
  isAllSelected,
  isIndeterminate,
}) => {
  const applicantStatusType: SelectOptions[] = [
    { value: 'Outstanding', label: 'Outstanding' },
    { value: 'Suitable', label: 'Suitable' },
    { value: 'Auto-Assessment', label: 'Auto-Assessment' },
    { value: 'New', label: 'New' },
    { value: 'Shortlisted', label: 'Shortlisted' },
  ];
  const assessmentStatusType: SelectOptions[] = [
    { value: 'completed', label: 'Completed' },
    { value: 'assigned', label: 'Assigned' },
    { value: 'expired', label: 'Expired' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'scoring', label: 'Scoring' },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box
        sx={{
          py: 3,
          px: 1.5,
          bgcolor: 'grey.50',
          borderRadius: 3,
        }}
      >
        <Grid container spacing={2} alignItems='flex-end'>
          <Grid item xs={12} sm={6} md={2}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <Typography variant='caption' sx={{ fontWeight: 700, color: 'text.secondary', ml: 0.5 }}>
                Name Search
              </Typography>
              <TextField
                size='small'
                fullWidth
                placeholder='Search by name...'
                value={localName}
                onChange={(e) => setLocalName(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <SearchIcon size={16} color='#9ca3af' />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <Typography variant='caption' sx={{ fontWeight: 700, color: 'text.secondary', ml: 0.5 }}>
                Email Search
              </Typography>
              <TextField
                size='small'
                fullWidth
                placeholder='Search by email...'
                value={localEmail}
                onChange={(e) => setLocalEmail(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>
                      <SearchIcons size={16} color='#9ca3af' />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <Typography variant='caption' sx={{ fontWeight: 700, color: 'text.secondary', ml: 0.5 }}>
                Applicant Status
              </Typography>
              <CustomSelect
                placeholder='All Statuses'
                name='applicantStatus'
                options={applicantStatusType}
                value={candidateInviteObject.applicantStatus.map((v: string) => ({
                  label: v,
                  value: v,
                }))}
                onChange={(opt: SelectOptions[]) =>
                  handleStatusListChange('applicantStatus', opt)
                }
                isMulti
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              <Typography variant='caption' sx={{ fontWeight: 700, color: 'text.secondary', ml: 0.5 }}>
                Assessment Status
              </Typography>
              <CustomSelect
                placeholder='All Assessment'
                name='assessmentStatus'
                options={assessmentStatusType}
                value={candidateInviteObject.assessmentStatus.map((v: string) => ({
                  label: v,
                  value: v,
                }))}
                onChange={(opt: SelectOptions[]) =>
                  handleStatusListChange('assessmentStatus', opt)
                }
                isMulti
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Button
              variant='primary'
              onClick={handleSendInvites}
              disabled={loading || selectedCandidates.length === 0}
              sx={{ height: 40, borderRadius: 2, width: '100%' }}
              label={`Send Invites${
                selectedCandidates.length > 0
                  ? ` (${selectedCandidates.length})`
                  : ''
              }`}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2}>
            <Button
              variant='secondary'
              onClick={() => handleRemoveCandidate()}
              disabled={loading || selectedCandidates.length === 0}
              sx={{ height: 40, borderRadius: 2, width: '100%' }}
              label='Remove Invite'
            />
          </Grid>
        </Grid>
      </Box>

      {candidatesList.length ? (
        <DataTable
          isLoading={loading}
          columns={getColumns((row) => handleRemoveCandidate(row.id))}
          data={candidatesList}
          rowsPerPage={limit}
          onPageChange={onPageChange}
          currentPage={pageNumber}
          totalPages={totalPages}
          onSortChange={handleSortChange}
          sortConfig={Object.entries(sort).map(([key, dir]) => ({
            key,
            direction: dir,
          }))}
          hasCheckBox
          onSelectAll={handleSelectAll}
          onSelectRow={handleSelectRow}
          isRowSelected={isRowSelected}
          isAllSelected={isAllSelected}
          isIndeterminate={isIndeterminate}
          totalRecords={totalRecords}
        />
      ) : (
        <Box sx={{ textAlign: 'center', py: 5 }}>
          <Typography variant='h6' sx={{ color: 'text.primary' }}>
            No Applicants
          </Typography>
        </Box>
      )}
    </Box>
  );
};
